import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Calendar,
  CheckCircle2,
  XCircle,
  Star,
  Users,
  Truck as TruckIcon,
  Building2,
  ChevronDown,
  Sparkles,
  FileSignature,
  Rocket,
  MessageSquare,
  ClipboardCheck,
} from "lucide-react";
import { LINKS } from "@/config/links";

const fmt = (n) => `$${n.toLocaleString("en-US")}`;

// ============ PRICING DATA ============
const PLANS = {
  truck1: {
    label: "1 Truck",
    audience: "Owner-Operator · 1 power unit",
    tiers: [
      {
        key: "mtm",
        name: "Month-to-Month",
        price: 499,
        term: "Cancel at the end of the current month",
        featured: false,
        planValue: "truck1_mtm",
      },
      {
        key: "6mo",
        name: "6-Month",
        price: 399,
        term: "6-month term. 30-day notice after the term. 2-month buyout to leave early.",
        featured: true,
        badge: "How most clients start",
        planValue: "truck1_6mo",
      },
      {
        key: "12mo",
        name: "12-Month",
        price: 349,
        term: "12-month term. 30-day notice after the term. 2-month buyout to leave early.",
        featured: false,
        badge: "Best value",
        planValue: "truck1_12mo",
      },
    ],
  },
  fleet: {
    label: "2–5 Trucks",
    audience: "Small fleet · 2–5 power units",
    tiers: [
      {
        key: "mtm",
        name: "Month-to-Month",
        price: 649,
        term: "Cancel at the end of the current month",
        featured: false,
        planValue: "fleet_mtm",
      },
      {
        key: "6mo",
        name: "6-Month",
        price: 499,
        term: "6-month term. 30-day notice after the term. 2-month buyout to leave early.",
        featured: true,
        badge: "How most clients start",
        planValue: "fleet_6mo",
      },
      {
        key: "12mo",
        name: "12-Month",
        price: 449,
        term: "12-month term. 30-day notice after the term. 2-month buyout to leave early.",
        featured: false,
        badge: "Best value",
        planValue: "fleet_12mo",
      },
    ],
  },
};

const MTM_INCLUDED = [
  "Monthly bookkeeping",
  "Bank & credit card reconciliations",
  "Monthly P&L and Balance Sheet",
  "Secure client portal",
  "Email support",
  "1099 contractor & W-9 tracking (not e-file)",
];

const MTM_NOT_INCLUDED = [
  "Setup / catch-up (quoted separately)",
  "IFTA preparation (quoted separately)",
];

const SIXMO_EXTRAS = [
  "New-client setup included",
  "Up to 3 months of catch-up bookkeeping",
  "IFTA monthly tracking and quarterly prep *",
];

const TWELVEMO_EXTRAS = [
  "Everything in the 6-month plan, plus:",
  "Up to 6 months of catch-up bookkeeping",
  "Quarterly video review",
];

const NOT_INCLUDED_GLOBAL = [
  "Form 2290 / HVUT — we do not prepare or file this",
  "Personal or business tax returns",
  "Payroll",
  "1099 preparation & e-filing",
  "IFTA when there is no ELD miles-by-state export (custom)",
  "Filing IFTA for the client unless they later give portal access (default: we prepare, they file)",
  "CFO / advisory engagements",
  "Catch-up beyond the contracted plan",
];

const WHO_WE_SERVE = [
  { icon: TruckIcon, title: "Owner-Operators", body: "One truck, one operator, one clean set of books every month." },
  { icon: Users, title: "Growing Operations", body: "You’re adding trucks, adding contractors, adding complexity. We keep it organized." },
  { icon: Building2, title: "Fleet Owners 1–5", body: "Small fleets running one to five power units who need real financial visibility." },
];

const ONBOARDING_STEPS = [
  { icon: MessageSquare, n: "01", t: "30-Minute Call", d: "See if Linehaul is a fit. We walk through your trucks, ELD, and where the books are today." },
  { icon: FileSignature, n: "02", t: "Engagement Letter", d: "Sign the engagement and pay. Work does not start until this is done. Term is month-to-month, 6-month, or 12-month, as chosen." },
  { icon: ClipboardCheck, n: "03", t: "Organizer + Access", d: "Fill the organizer and grant QBO, bank, and ELD access so we have what we need." },
  { icon: Rocket, n: "04", t: "First month done", d: "We close the first month on schedule. You’re rolling." },
];

const FAQS = [
  {
    q: "Do you handle taxes or IFTA?",
    a: "We do not prepare or file income tax returns. On the 6-month plan and the 12-month plan we prepare a quarterly IFTA return from your ELD miles and fuel records. You file it, or we file it if you give us portal access.",
  },
  {
    q: "Can I cancel?",
    a: "Month-to-month cancels at the end of the current month. The 6-month plan and the 12-month plan require 30-day notice after the term. To leave the term early, there is a 2-month buyout.",
  },
  {
    q: "What’s the call like?",
    a: "Thirty minutes. Fit, number of trucks, whether you’re on Motive / Samsara / another ELD, whether books already exist, and which term makes sense. Not a pitch deck.",
  },
  {
    q: "What if my books are behind?",
    a: "Catch-up is included up to 3 months on the 6-month plan and up to 6 months on the 12-month plan. Beyond that — or on month-to-month — we quote it separately.",
  },
  {
    q: "What software do you use?",
    a: "QuickBooks Online is our system of record. If you’re not already on QBO, we help you get set up.",
  },
  {
    q: "Do you offer payroll?",
    a: "Payroll is not part of the monthly engagement. It’s handled as a Custom Engagement.",
  },
  {
    q: "More than 5 trucks?",
    a: "Six or more power units is a Custom Engagement. Scoped and priced on the call.",
  },
];

export default function Services() {
  const [audience, setAudience] = useState("truck1"); // 'truck1' | 'fleet'

  const current = PLANS[audience];

  return (
    <div data-testid="page-services">
      {/* HERO */}
      <section className="relative pt-20 sm:pt-28 pb-10 sm:pb-12 overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[420px] gradient-radial-blue pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">Services &amp; Pricing</p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-white leading-[1.05]">
              Specialized bookkeeping. <span className="text-[#FFCB05]">Built for the road.</span>
            </h1>
            <p className="mt-5 max-w-2xl text-base sm:text-lg text-[#9CA3AF] leading-relaxed">
              Bookkeeping for owner-operators and fleets running 1–5 trucks. Month-to-month exists. Six months is how most clients start &mdash; setup, catch-up, and IFTA prep are in it. Thirty-minute call first.
            </p>
          </motion.div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="relative py-16 bg-[#0A111E] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">Who we serve</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-medium tracking-tight text-white">Made for 1–5 power units.</h2>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {WHO_WE_SERVE.map((w, i) => (
              <motion.div
                key={w.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="p-7 rounded-2xl border border-white/5 bg-[#040914]"
              >
                <span className="inline-grid place-items-center h-11 w-11 rounded-xl bg-[#00274C] border border-white/10">
                  <w.icon className="h-5 w-5 text-[#FFCB05]" />
                </span>
                <h3 className="mt-5 font-display text-lg font-semibold text-white">{w.title}</h3>
                <p className="mt-2 text-sm text-[#9CA3AF] leading-relaxed" dangerouslySetInnerHTML={{ __html: w.body }} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="relative py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">Pricing</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white">
              Six months is how most clients start.
            </h2>
            <p className="mt-4 text-[#9CA3AF]">Month-to-month is the on-ramp. Twelve months is the best rate. Pick your fleet size below.</p>
          </div>

          {/* Fleet size toggle */}
          <div className="mt-10 inline-flex p-1 rounded-full border border-white/10 bg-[#0A111E]" data-testid="fleet-toggle">
            {Object.entries(PLANS).map(([key, plan]) => (
              <button
                key={key}
                type="button"
                onClick={() => setAudience(key)}
                data-testid={`fleet-toggle-${key}`}
                className={`relative px-5 py-2 rounded-full text-xs sm:text-sm font-semibold tracking-wide transition-colors ${
                  audience === key ? "text-[#040914]" : "text-[#9CA3AF] hover:text-white"
                }`}
              >
                {audience === key && (
                  <motion.span
                    layoutId="fleet-pill"
                    className="absolute inset-0 bg-[#FFCB05] rounded-full"
                    transition={{ type: "spring", stiffness: 400, damping: 32 }}
                  />
                )}
                <span className="relative">{plan.label}</span>
              </button>
            ))}
          </div>

          {/* Pricing cards */}
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
            {current.tiers.map((tier, i) => (
              <TierCard
                key={`${audience}-${tier.key}`}
                tier={tier}
                audience={current.audience}
                index={i}
              />
            ))}
          </div>

          {/* IFTA callout */}
          <div className="mt-10 rounded-2xl border border-[#FFCB05]/30 bg-[#FFCB05]/5 p-6 sm:p-7" data-testid="ifta-callout">
            <div className="flex items-start gap-4">
              <span className="inline-grid place-items-center h-10 w-10 rounded-xl bg-[#040914] border border-[#FFCB05]/30 shrink-0">
                <TruckIcon className="h-5 w-5 text-[#FFCB05]" />
              </span>
              <div>
                <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">* On IFTA</p>
                <p className="mt-1.5 text-sm text-[#E5E7EB] leading-relaxed">
                  IFTA quarterly prep is included on the 6-month plan and the 12-month plan if your ELD already reports miles by state (Motive, Samsara, or similar). We match that to your fuel cards and prepare the return. It is not included on month-to-month.
                </p>
              </div>
            </div>
          </div>

          {/* Contract callout */}
          <div className="mt-4 rounded-2xl border border-white/10 bg-[#0A111E] p-6 sm:p-7" data-testid="contract-callout">
            <div className="flex items-start gap-4">
              <span className="inline-grid place-items-center h-10 w-10 rounded-xl bg-[#00274C] border border-white/10 shrink-0">
                <FileSignature className="h-5 w-5 text-[#FFCB05]" />
              </span>
              <div>
                <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">On contracts</p>
                <p className="mt-1.5 text-sm text-[#E5E7EB] leading-relaxed">
                  Month-to-month is there if you need to try us. Setup, catch-up, and IFTA prep are in the 6-month plan. The 12-month plan is the lowest rate.
                </p>
              </div>
            </div>
          </div>

          {/* Custom card */}
          <div className="mt-8">
            <CustomCard />
          </div>

          <p className="mt-8 text-sm text-[#9CA3AF]">
            Not sure which plan fits?{" "}
            <a href={LINKS.discoveryCall} target="_blank" rel="noreferrer" data-testid="pricing-help-calendly" className="text-[#FFCB05] hover:underline">
              See if Linehaul is a fit (30 min)
            </a>{" "}
            and we’ll walk through it together.
          </p>
        </div>
      </section>

      {/* NOT INCLUDED (global) */}
      <section className="relative py-16 sm:py-20 bg-[#0A111E] border-y border-white/5">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">Not included</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-medium tracking-tight text-white">The plain-English list.</h2>
          <p className="mt-4 text-[#9CA3AF]">If it’s on this list, it’s not in the monthly engagement. Some are available as a Custom Engagement.</p>

          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="not-included-list">
            {NOT_INCLUDED_GLOBAL.map((f) => (
              <li key={f} className="flex items-start gap-3 p-4 rounded-xl bg-[#040914] border border-white/5 text-sm text-[#E5E7EB]">
                <XCircle className="mt-0.5 h-4 w-4 text-[#6B7280] shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">How it works</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white">Four steps. No pitch deck.</h2>
            <p className="mt-4 text-[#9CA3AF]">The inquiry form is optional. The primary path is the calendar.</p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {ONBOARDING_STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                data-testid={`onboarding-step-${s.n}`}
                className="relative p-6 rounded-2xl border border-white/5 bg-[#0A111E]"
              >
                <div className="flex items-center justify-between">
                  <span className="inline-grid place-items-center h-9 w-9 rounded-lg bg-[#00274C] border border-white/10">
                    <s.icon className="h-4 w-4 text-[#FFCB05]" />
                  </span>
                  <span className="font-display text-3xl font-medium text-[#FFCB05]/50">{s.n}</span>
                </div>
                <h3 className="mt-4 font-display text-base font-semibold text-white">{s.t}</h3>
                <p className="mt-1.5 text-xs text-[#9CA3AF] leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="relative py-20 sm:py-24 bg-[#0A111E] border-y border-white/5">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">Frequently asked</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white">Questions we hear a lot.</h2>
          </div>

          <div className="mt-10 space-y-3">
            {FAQS.map((f, i) => <Faq key={i} q={f.q} a={f.a} index={i} />)}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-24">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#00274C] via-[#0A111E] to-[#040914] p-10 sm:p-14">
            <div className="absolute -top-24 -right-20 h-72 w-72 rounded-full bg-[#FFCB05]/15 blur-3xl" />
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">Ready to start?</p>
                <h3 className="mt-3 font-display text-3xl sm:text-4xl font-medium tracking-tight text-white">Book the 30-minute call.</h3>
                <p className="mt-3 text-[#9CA3AF]">Thirty minutes. Fit, trucks, ELD, term. Not a pitch deck.</p>
              </div>
              <div className="flex flex-wrap gap-3 md:justify-end">
                <a
                  href={LINKS.discoveryCall}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="services-book-call-btn"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold bg-[#FFCB05] text-[#040914] hover:bg-[#FFD633] transition-all hover:-translate-y-0.5"
                >
                  See if Linehaul is a fit <Calendar className="h-4 w-4" />
                </a>
                <Link
                  to="/inquiry"
                  data-testid="services-inquiry-btn"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold border border-white/15 text-white hover:border-[#FFCB05]/50 hover:bg-white/5 transition-all"
                >
                  Send an Inquiry <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ============ TIER CARD ============
function TierCard({ tier, audience, index }) {
  const isSixMo = tier.key === "6mo";
  const isTwelveMo = tier.key === "12mo";
  const isMtm = tier.key === "mtm";

  const included = isMtm
    ? MTM_INCLUDED
    : isSixMo
    ? [...MTM_INCLUDED, ...SIXMO_EXTRAS]
    : MTM_INCLUDED;

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, delay: index * 0.07 }}
      data-testid={`tier-card-${tier.planValue}`}
      className={`relative flex flex-col p-8 rounded-3xl transition-all duration-300 ${
        tier.featured
          ? "bg-gradient-to-b from-[#00274C] to-[#040914] border border-[#FFCB05]/50 maize-glow lg:-translate-y-3"
          : "bg-gradient-to-b from-[#00274C]/40 to-[#040914] border border-white/5 hover:border-[#FFCB05]/30 hover:-translate-y-1"
      }`}
    >
      {tier.badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span
            data-testid={`tier-badge-${tier.planValue}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-semibold tracking-[0.18em] uppercase ${
              tier.featured ? "bg-[#FFCB05] text-[#040914]" : "bg-[#0A111E] text-[#FFCB05] border border-[#FFCB05]/40"
            }`}
          >
            <Star className="h-3 w-3" strokeWidth={2.5} /> {tier.badge}
          </span>
        </div>
      )}

      <div>
        <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">{tier.name}</p>
        <div className="mt-3 flex items-baseline gap-1.5">
          <span className="font-display text-5xl font-medium tracking-tight text-white" data-testid={`tier-price-${tier.planValue}`}>
            {fmt(tier.price)}
          </span>
          <span className="text-sm text-[#9CA3AF]">/mo</span>
        </div>
        <p className="mt-2 text-xs text-[#6B7280] leading-relaxed">{tier.term}</p>
      </div>

      {/* Included */}
      <div className="mt-7 flex-1">
        <p className="text-xs font-semibold tracking-[0.12em] uppercase text-[#9CA3AF] mb-3">Included</p>
        <ul className="space-y-2.5">
          {included.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-sm text-[#E5E7EB]">
              <CheckCircle2 className="mt-0.5 h-4 w-4 text-[#FFCB05] shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        {isTwelveMo && (
          <div className="mt-5 pt-5 border-t border-white/10">
            <ul className="space-y-2.5">
              {TWELVEMO_EXTRAS.map((f, i) => (
                <li key={f} className={`flex items-start gap-2.5 text-sm ${i === 0 ? "text-[#FFCB05] font-medium" : "text-[#E5E7EB]"}`}>
                  {i === 0 ? (
                    <span className="mt-0.5 h-4 w-4 shrink-0" />
                  ) : (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 text-[#FFCB05] shrink-0" />
                  )}
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {isMtm && (
          <div className="mt-5 pt-5 border-t border-white/10">
            <p className="text-xs font-semibold tracking-[0.12em] uppercase text-[#6B7280] mb-3">Not in this plan</p>
            <ul className="space-y-2">
              {MTM_NOT_INCLUDED.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-[#6B7280]">
                  <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="mt-8">
        <a
          href={LINKS.discoveryCall}
          target="_blank"
          rel="noreferrer"
          data-testid={`tier-cta-${tier.planValue}`}
          className={`flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold transition-all hover:-translate-y-0.5 ${
            tier.featured
              ? "bg-[#FFCB05] text-[#040914] hover:bg-[#FFD633]"
              : "bg-[#FFCB05] text-[#040914] hover:bg-[#FFD633]"
          }`}
        >
          See if Linehaul is a fit <ArrowRight className="h-4 w-4" />
        </a>
        <p className="mt-2 text-[10px] text-center text-[#6B7280] tracking-[0.12em] uppercase">
          {audience}
        </p>
      </div>
    </motion.article>
  );
}

// ============ CUSTOM CARD ============
function CustomCard() {
  return (
    <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#0A111E] to-[#040914] p-8 sm:p-10" data-testid="tier-card-custom">
      <div className="grid md:grid-cols-3 gap-8 items-start">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <span className="inline-grid place-items-center h-11 w-11 rounded-xl bg-[#040914] border border-white/10">
              <Sparkles className="h-5 w-5 text-[#FFCB05]" />
            </span>
            <div>
              <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">Custom Engagement</p>
              <h3 className="font-display text-2xl font-semibold text-white">Priced on the call.</h3>
            </div>
          </div>

          <p className="mt-5 text-sm text-[#9CA3AF] leading-relaxed">
            For operations outside the standard 1–5 truck engagement. Examples:
          </p>
          <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
              "6+ trucks",
              "No ELD or manual mileage",
              "Payroll",
              "1099 preparation & e-filing",
              "Personal or business tax returns (referral network)",
              "Catch-up beyond the contracted plan",
              "Multi-entity bookkeeping",
              "Special bookkeeping projects",
            ].map((ex) => (
              <li key={ex} className="flex items-start gap-2 text-sm text-[#E5E7EB]">
                <CheckCircle2 className="mt-0.5 h-4 w-4 text-[#FFCB05] shrink-0" />
                <span>{ex}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col gap-3">
          <a
            href={LINKS.discoveryCall}
            target="_blank"
            rel="noreferrer"
            data-testid="custom-book-call-btn"
            className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold bg-[#FFCB05] text-[#040914] hover:bg-[#FFD633] transition-all hover:-translate-y-0.5"
          >
            See if Linehaul is a fit <ArrowRight className="h-4 w-4" />
          </a>
          <Link
            to="/inquiry?custom=true"
            data-testid="custom-inquiry-btn"
            className="flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold border border-white/15 text-white hover:border-[#FFCB05]/50 hover:bg-white/5 transition-all"
          >
            Send an Inquiry
          </Link>
        </div>
      </div>
    </div>
  );
}

// ============ FAQ ============
function Faq({ q, a, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="rounded-2xl border border-white/5 bg-[#040914]">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`services-faq-toggle-${index}`}
        className="w-full flex items-center justify-between px-6 py-5 text-left gap-4"
      >
        <span className="text-sm sm:text-base font-medium text-white">{q}</span>
        <ChevronDown className={`h-4 w-4 text-[#FFCB05] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-6 pb-5 text-sm text-[#9CA3AF] leading-relaxed">{a}</div>
      )}
    </div>
  );
}
